// ═══════════════════════════════════════════════════════════════════════════
// QC CONFIG SERVICE — loads client profiles from qcProfiles.json
// ═══════════════════════════════════════════════════════════════════════════
//
// Usage in any run file:
//   import { getConfig } from '../taskpane/configService';
//   const cfg = getConfig();
//   const font = cfg.run1_font.targetFont;  // "Times New Roman" or "Arial"
//
// Usage in taskpane.ts for profile switching:
//   import { getActiveProfileName, setActiveProfile, getProfileNames } from '../taskpane/configService';
//   const names = getProfileNames();        // ["takeda", "gilead"]
//   setActiveProfile("gilead");             // switches at runtime
// ═══════════════════════════════════════════════════════════════════════════

import profileData from "./qcProfiles.json";

// ─── Type definitions for each run's config ──────────────────────────────

export interface FontConfig {
  targetFont: string;
  symbolFonts: string[];
}

export interface MarginsConfig {
  topMargin: number;
  bottomMargin: number;
  leftMargin: number;
  rightMargin: number;
  headerDistance: number;
  footerDistance: number;
  gutter: number;
  gutterPosition: string;
  mirrorMargins: boolean;
  twoPagesOnOne: boolean;
  bookFoldPrinting: boolean;
  oddAndEvenPagesHeaderFooter: boolean;
}

export interface TableCaptionConfig {
  captionStyle: string;
  headerStyle: string;
  captionRegex: string;
  captionMustNotEndWith: string;
}

export interface HyphensConfig {
  productCodePrefix: string;
  productCodeRegex: string;
  replaceWithNonBreaking: boolean;
}

export interface SpacesConfig {
  removeDoubleSpaces: boolean;
  addMissingSpaceAfterPeriod: boolean;
  maxPasses: number;
}

export interface BodyTextConfig {
  targetStyle: string;
  excludeStylePatterns: string[];
  convertStyles: string[];
}

export interface BulletStyleLevels {
  level1: string;
  level2: string;
  level3?: string;
}

export interface BodyBulletsConfig {
  bulletStyles: BulletStyleLevels;
  numberStyles: { level1: string; level2: string };
  alphaStyles: { level1: string; level2: string };
}

export interface FiguresConfig {
  captionStyle: string;
  bodyTextStyle: string;
  figureRegex: string;
}

export interface FootnoteConfig {
  footnoteStyle: string;
  footnoteFont?: string;
  footnoteSize?: number;
}

export interface HeadingsConfig {
  headingFont: string;
  headingSize: number;
  maxHeadingLevel: number;
}


export interface TableTextConfig {
  tableTextStyle: string;
  tableTextFont: string;
  tableTextSize: number;
  charResetStyle: string;
   headerStylePrefix: string;
  headerDetectBold: boolean;
  captionRegex: string;
  captionMustNotEndWith: string;
  captionStopPatterns: string[];
  skipStylePatterns: string[];
  excludeLastRowIfSingleCell: boolean;
}

export interface LOTConfig {
  headingStyle:    string;  
  headingStyleXml: string;  
  searchPhrases:   string[];
  lookaheadPrefix: string;
}

export interface LOFConfig {
  headingStyle:    string;  
  headingStyleXml: string;  
  searchPhrases:   string[];
  lookaheadPrefix: string;
}

export interface LOAConfig {
  headingStyle:    string; 
  headingStyleXml: string; 
  baseStyle:       string;  
  searchPhrases:   string[];
  lookaheadPrefix: string;
}

export interface AppendixConfig {
  appendixStyle: string;   
  appendixRegex: string;
  qcAuthor:      string;  
  qcDate:        string;  
  trackIdStart:  number;  
}
export interface SpacingDef {
  before: number;
  after: number;
  line: number;
}

export interface ParaFormatConfig {
  bodyTextSpacing: SpacingDef;
  headingSpacing: SpacingDef;
  captionSpacing: SpacingDef;
  removeAutoSpacing: boolean;
  deleteEmptyParas: boolean;
}

export interface HeaderFormatConfig {
  headerFont:      string;
  headerSize:      number;
  headerBold:      boolean;
  headerStyle:     string;
  headerCharStyle: string;
  borderType:      string;
  borderWidth:     number;
  borderColor:     string;
}
export interface FooterFormatConfig {
  footerFont:      string;
  footerSize:      number;
  footerBold:      boolean;
  footerAlignment: string;
  footerStyle:     string;
}
export interface ConsistentHeaderConfig {
  enforceConsistency: boolean;

  monthPattern:       string; 
  yearPattern:        string; 
  pagePattern:        string;
  pageCapturePattern: string;  
  headerTypesToCheck: Array<"primary" | "firstPage" | "evenPages">;
  inconsistencyCommentText: string;
}

export interface RemoveFooterConfig {
  removeFromSections: string;
  preserveText:       string;
  delAuthor:          string;
}

export interface ConfidentialConfig {
  confidentialText: string;
  position: string;
  confistyle: string;
  alignment: string;
  bold: boolean
}

export interface SectionSpacingConfig {
  sectionBreakType: string;
  target_space_before: number;
  target_space_after: number;
  heading_style_pattern: string;
  toc_style_prefixes : string;
  toc_style_exact: string;
  bookmark_prefixes: string;
  bookmark_regex: string;
  ooxml: {paragraph_block_regex:string, spacing_tag_regex:string}
}

export interface XrefPatternDef {
  label: string;
  regex: string;
}

export interface CrossReferencesConfig {
  patterns: XrefPatternDef[];
  tocCaptionStyles: string[];
}

export interface TableListBulletsConfig {
  bulletStyles: BulletStyleLevels;
  numberStyle: string;
  alphaStyle: string;
}

export interface TableDefaultsConfig {
  defaultFont: string;
  defaultSize: number;
  cellPadding: { top: number; bottom: number; left: number; right: number };
  tableWidthPct: number;
  tableAlignment : string;
  tableIndentTwips: number;
  layoutType : string;
  ooxmlInsertMode : string;
}

// ─── Full profile type ───────────────────────────────────────────────────

export interface QCProfile {
  clientName: string;
  description: string;

  run1_font: FontConfig;
  run2_margins: MarginsConfig;
  run3_tableCaption: TableCaptionConfig;
  run4_hyphens: HyphensConfig;
  run5_spaces: SpacesConfig;
  run6_bodyText: BodyTextConfig;
  run7_bodyBullets: BodyBulletsConfig;
  run8_figures: FiguresConfig;
  run9_figureFootnotes: FootnoteConfig;
  run10_headings: HeadingsConfig;
  run11_tableText: TableTextConfig;
  run12_tableFootnotes: FootnoteConfig;
  run13_LOT: LOTConfig;
  run14_LOF: LOFConfig;
  run15_LOA: LOAConfig;
  run16_appendix: AppendixConfig;
  run17_paraFormat: ParaFormatConfig;
  run18_header: HeaderFormatConfig;
  run19_footer: FooterFormatConfig;
  run20_consistentHeader: ConsistentHeaderConfig;
  run21_removeFooter: RemoveFooterConfig;
  run22_addConfidential: ConfidentialConfig;
  run23_sectionSpacing: SectionSpacingConfig;
  run24_crossReferences: CrossReferencesConfig;
  run25_tableListBullets: TableListBulletsConfig;
  run26_tableDefaults: TableDefaultsConfig;
}

// ─── Internal state ──────────────────────────────────────────────────────

const data = profileData as any;
let activeProfileName: string = data.activeProfile || "takeda";

// ─── Public API ──────────────────────────────────────────────────────────

/** Get the currently active profile config (typed). */
export function getConfig(): QCProfile {
  const profile = data.profiles[activeProfileName];
  if (!profile) {
    console.error(`[Config] Profile "${activeProfileName}" not found. Falling back to first available.`);
    const keys = Object.keys(data.profiles);
    if (keys.length === 0) throw new Error("No QC profiles defined in qcProfiles.json");
    activeProfileName = keys[0];
    return data.profiles[activeProfileName] as QCProfile;
  }
  return profile as QCProfile;
}

/** Get the name of the active profile. */
export function getActiveProfileName(): string {
  return activeProfileName;
}

/** Get list of all available profile names. */
export function getProfileNames(): string[] {
  return Object.keys(data.profiles);
}

/** Switch to a different profile at runtime. */
export function setActiveProfile(name: string): void {
  if (!data.profiles[name]) {
    console.error(`[Config] Profile "${name}" not found. Available: ${getProfileNames().join(", ")}`);
    return;
  }
  activeProfileName = name;
  console.log(`[Config] Switched to profile: "${name}" (${data.profiles[name].clientName})`);
}

/** Get a specific profile by name (without switching). */
export function getProfile(name: string): QCProfile | null {
  return (data.profiles[name] as QCProfile) || null;
}
